import { BrowserAuthError, InteractionRequiredAuthError } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { apiRequest, silentRedirectUri } from "./msalConfig";

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "/api";

// Shared fetch helper backed by MSAL silent token acquisition. Three flavours:
//   - request<T>(path, init?)       JSON in, JSON (or 204 empty) out
//   - requestForm<T>(path, form)    multipart form-data (uploads), JSON out
//   - requestBlob(path, init?)      binary out (file downloads)
// All three throw on non-2xx with the response body in the message.
export function useApiClient() {
  const { instance, accounts } = useMsal();

  async function getToken(): Promise<string> {
    const account = accounts[0];
    if (!account) throw new Error("not authenticated");
    try {
      const result = await instance.acquireTokenSilent({
        ...apiRequest,
        account,
        redirectUri: silentRedirectUri,
      });
      return result.accessToken;
    } catch (err) {
      if (!needsInteractiveRenewal(err)) throw err;
      // La renovación silenciosa ya no puede más (token de refresco de 24 h
      // caducado, cookies de terceros bloqueadas, iframe sin respuesta...):
      // renovar la sesión de forma visible. Navega a Microsoft y vuelve a esta
      // misma página; el clic que disparó la llamada hay que repetirlo.
      await instance.acquireTokenRedirect({ ...apiRequest, account });
      throw new Error("Renovando la sesión con Microsoft…");
    }
  }

  async function readErrorBody(res: Response): Promise<string> {
    const contentType = res.headers.get("content-type") ?? "";
    if (contentType.includes("application/json")) {
      const payload = (await res.json()) as { code?: string; message?: string; error?: string; details?: unknown };
      const message = payload.message ?? payload.error ?? JSON.stringify(payload.details ?? payload);
      return payload.code ? `[${payload.code}] ${message}` : message;
    }
    return res.text();
  }

  async function throwHttpError(res: Response): Promise<never> {
    const body = await readErrorBody(res);
    throw new Error(`${res.status} ${res.statusText}: ${body}`);
  }

  async function handleResponse<T>(res: Response): Promise<T> {
    if (!res.ok) await throwHttpError(res);
    if (res.status === 204) return undefined as T;

    const contentType = res.headers.get("content-type") ?? "";
    if (!contentType.includes("application/json")) {
      const text = await res.text();
      return (text ? (text as unknown) : undefined) as T;
    }

    return (await res.json()) as T;
  }

  async function request<T>(path: string, init?: RequestInit): Promise<T> {
    const token = await getToken();
    const res = await fetch(`${BASE_URL}${path}`, {
      ...init,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        ...(init?.headers ?? {}),
      },
    });

    return handleResponse<T>(res);
  }

  async function requestForm<T>(path: string, form: FormData): Promise<T> {
    const token = await getToken();
    // Do NOT set Content-Type: the browser computes a multipart boundary for us.
    const res = await fetch(`${BASE_URL}${path}`, {
      method: "POST",
      body: form,
      headers: { Authorization: `Bearer ${token}` },
    });

    return handleResponse<T>(res);
  }

  async function requestBlob(path: string, init?: RequestInit): Promise<Blob> {
    const token = await getToken();
    const res = await fetch(`${BASE_URL}${path}`, {
      ...init,
      headers: { Authorization: `Bearer ${token}`, ...(init?.headers ?? {}) },
    });

    if (!res.ok) await throwHttpError(res);
    return res.blob();
  }

  return { request, requestForm, requestBlob };
}

// Errores de acquireTokenSilent que se arreglan con un login visible. Los
// InteractionRequiredAuthError son la señal explícita de Microsoft; los
// BrowserAuthError son los fallos del propio iframe oculto (monitor_window_timeout
// cuando no responde a tiempo, hash vacío, iframe bloqueado...). Se excluye
// interaction_in_progress: ya hay un redirect en marcha y lanzar otro rompería.
export function needsInteractiveRenewal(err: unknown): boolean {
  if (err instanceof InteractionRequiredAuthError) return true;
  if (err instanceof BrowserAuthError) return err.errorCode !== "interaction_in_progress";
  return false;
}
